import { appNav } from "../../routes/appNavigation";

export type MobileHeader = {
  /** Shown in place of the brand wordmark. */
  title: string;
  /**
   * Where the back chevron goes, or null on a tab-bar root where there is
   * nothing to go back to and a chevron would be a dead end.
   */
  backTo: string | null;
};

/**
 * The bottom tab bar already says where you are among the five roots, so the
 * header's job on mobile is the opposite: name the specific screen you drilled
 * into and offer the way back out. A static "Zoniq EventOS" wordmark does
 * neither, and costs the full width of the screen to say nothing.
 *
 * Matched longest-prefix-first so nested routes win over their parents.
 */
const ROUTES: { match: RegExp; title: string; backTo: string | null }[] = [
  // Tab-bar roots — no back affordance.
  { match: /^\/calendar$/, title: "Calendar", backTo: null },
  { match: /^\/events$/, title: "Events", backTo: null },
  { match: /^\/directory\/guests$/, title: "Guest List", backTo: null },

  // Event workspace. The layout renders its own event title and tabs, so the
  // header only needs to name the section and get back to the list.
  { match: /^\/events\/new$/, title: "New Event", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/edit$/, title: "Edit Event", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/overview$/, title: "Overview", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/deals$/, title: "Deals", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/budget$/, title: "Budget & Costs", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/pnl$/, title: "P&L", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/run-of-show$/, title: "Run of Show", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/tickets$/, title: "Tickets", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/live$/, title: "Live Ops", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/rsvp$/, title: "RSVP", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/invoices/, title: "Invoices", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/contacts$/, title: "Contacts", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/notes$/, title: "Notes", backTo: appNav.events() },
  { match: /^\/events\/[^/]+\/files$/, title: "Files", backTo: appNav.events() },

  // Reached through "More", so back returns to the calendar root.
  { match: /^\/finance\/analytics$/, title: "Analytics", backTo: appNav.finance() },
  { match: /^\/finance\/events$/, title: "Event Finance", backTo: appNav.finance() },
  { match: /^\/finance\/invoices$/, title: "Invoices", backTo: appNav.finance() },
  { match: /^\/finance$/, title: "Finance", backTo: appNav.calendar() },
  { match: /^\/directory\/artists$/, title: "Artists", backTo: appNav.calendar() },
  { match: /^\/directory\/venues$/, title: "Venues", backTo: appNav.calendar() },
  { match: /^\/directory\/contacts$/, title: "Contacts", backTo: appNav.calendar() },
  { match: /^\/admin\/ticketing-links$/, title: "zoniq.in Links", backTo: appNav.calendar() },
  { match: /^\/profile$/, title: "Profile", backTo: appNav.calendar() },
  { match: /^\/home$/, title: "Home", backTo: null },
];

export function mobileHeaderFor(pathname: string): MobileHeader {
  const hit = ROUTES.find((r) => r.match.test(pathname));
  // An unmapped route still gets a usable header rather than an empty bar.
  return hit
    ? { title: hit.title, backTo: hit.backTo }
    : { title: "Zoniq EventOS", backTo: null };
}
